import React from 'react';
import logo from '../assets/logo.png';
import AdBanner from '../components/AdBanner';
import WatchlistButton from '../components/WatchlistButton';

const Sidebar = ({
  companyName,
  sharePrice,
  sharePriceTimestamp,
  stockSymbol,
  changePercent,
  change,
  marketCap,
  high52Week,
  low52Week,
  beta,
  trailingPE,
  forwardPE,
  pegRatio,
  epsTTM,
  revenueGrowth,
  dividendYield,
  dataRefreshed
}) => {
  return (
    <div className="w-full md:w-[220px] md:self-start space-y-4 mt-4">
      {/* Company info card */}
      <div className="card p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-gray-900 break-words">{companyName}</h2>
            <p className="text-xs text-gray-500 font-medium mt-0.5 break-words">
              {stockSymbol}
            </p>
          </div>
          {stockSymbol && (
            <WatchlistButton symbol={stockSymbol} companyName={companyName} />
          )}
        </div>
        <p className="text-2xl font-bold text-brand-600 mt-2 break-words">
          {formatSharePrice(sharePrice)}
        </p>
        {change != null && (
          <p className={`text-sm font-semibold mt-1 ${Number(change) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {Number(change) >= 0 ? '+' : ''}{formatCurrency(change)} ({Number(changePercent) >= 0 ? '+' : ''}{formatPercentage(changePercent)})
          </p>
        )}
        {sharePriceTimestamp && (
          <p className="text-xs text-gray-400 mt-1 break-words">
            Updated {sharePriceTimestamp}
          </p>
        )}
      </div>

      {/* Logo */}
      <div className="flex justify-center">
        <img
          src={logo}
          alt="Luminoso"
          className="h-20 w-20 md:h-24 md:w-24 object-contain opacity-80"
        />
      </div>

      {/* Key fundamentals - always visible */}
      <div className="card p-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Key Fundamentals</h3>
        <div className="text-sm text-gray-700 space-y-2 break-words">
          <MetricRow label="Market Cap" value={formatMarketCap(marketCap)} />
          <MetricRow label="Trailing P/E" value={formatRatio(trailingPE)} />
          <MetricRow label="Forward P/E" value={formatRatio(forwardPE)} />
          <MetricRow label="PEG Ratio" value={formatRatio(pegRatio)} />
          <MetricRow label="EPS (TTM)" value={formatCurrency(epsTTM)} />
          <MetricRow label="Rev Growth" value={revenueGrowth != null ? formatPercentage(Number(revenueGrowth) * 100) : 'N/A'} />
          <MetricRow label="Beta" value={formatRatio(beta)} />
          <MetricRow label="Div Yield" value={dividendYield != null ? formatPercentage(Number(dividendYield) * 100) : 'N/A'} />
          <div className="flex justify-between">
            <span className="text-gray-500">52-Wk Range</span>
            <span className="font-medium text-xs">
              {formatCurrency(low52Week)} – {formatCurrency(high52Week)}
            </span>
          </div>
        </div>
        {dataRefreshed && (
          <p className="text-[10px] text-gray-400 mt-3 pt-2 border-t border-gray-100">
            Data as of {formatRefreshedDate(dataRefreshed)}
          </p>
        )}
      </div>

      <div>
        <AdBanner />
      </div>
    </div>
  );
};

const MetricRow = ({ label, value }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);

const formatSharePrice = (price) => {
  if (price === 'N/A' || price === undefined || price === null) return 'N/A';
  const num = Number(price);
  return `$${num.toFixed(2)}`;
};

const formatCurrency = (value) => {
  const num = Number(value);
  return isNaN(num) ? 'N/A' : `$${num.toFixed(2)}`;
};

const formatMarketCap = (value) => {
  const num = Number(value);
  if (isNaN(num)) return 'N/A';
  if (num >= 1e12) return `$${(num / 1e12).toFixed(2)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}Bn`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(1)}Mn`;
  return `$${num.toLocaleString()}`;
};

const formatPercentage = (value) => {
  const num = Number(value);
  return isNaN(num) ? 'N/A' : `${num.toFixed(2)}%`;
};

const formatRatio = (value) => {
  const num = Number(value);
  return isNaN(num) ? 'N/A' : num.toFixed(2);
};

const formatRefreshedDate = (isoStr) => {
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return isoStr;
  }
};

export default Sidebar;
